/** How long Check installation waits for the SDK to reach AppNotes. */
export const INSTALLATION_CHECK_WINDOW_MS = 120_000;

/** How often the dialog re-reads the Site while it waits. */
export const INSTALLATION_CHECK_POLL_MS = 3_000;

/**
 * The service records a connection at most once a minute
 * (`SITE_CONNECTION_WRITE_INTERVAL_MS`), so a connection inside that window
 * before the check began can absorb the new one without moving the value.
 */
export const SITE_CONNECTION_COALESCE_MS = 60_000;

export type InstallationCheck =
  | { readonly kind: "idle" }
  | {
      readonly kind: "waiting";
      readonly siteId: string;
      readonly baseline: string | null;
      readonly startedAt: number;
    }
  | { readonly kind: "connected"; readonly siteId: string }
  | { readonly kind: "missed"; readonly siteId: string }
  | { readonly kind: "blocked"; readonly siteId: string }
  | {
      readonly kind: "failed";
      readonly siteId: string;
      readonly message: string;
    };

/** True once the Site's `last_connected_at` proves a connection for this check. */
export function connectionArrived(input: {
  readonly baseline: string | null;
  readonly current: string | null;
  readonly startedAt: number;
}): boolean {
  if (input.current === null) return false;
  if (input.current !== input.baseline) return true;
  const connected = Date.parse(input.current);
  return (
    Number.isFinite(connected) &&
    connected >= input.startedAt - SITE_CONNECTION_COALESCE_MS
  );
}

/** Which status line the Setup dialog shows for one Site. */
export type SetupStatus =
  | "archived"
  | "never"
  | "earlier"
  | "waiting"
  | "now"
  | "missed"
  | "blocked"
  | "failed";

export function setupStatus(
  site: {
    readonly id: string;
    readonly enabled: boolean;
    readonly last_connected_at?: string | null | undefined;
  },
  check: InstallationCheck,
): SetupStatus {
  if (!site.enabled) return "archived";
  if (check.kind !== "idle" && check.siteId === site.id) {
    switch (check.kind) {
      case "waiting":
        return "waiting";
      case "connected":
        return "now";
      case "missed":
        return "missed";
      case "blocked":
        return "blocked";
      case "failed":
        return "failed";
    }
  }
  return (site.last_connected_at ?? null) === null ? "never" : "earlier";
}
